import React from 'react';
import { Table, Pagination, Button, Card } from 'react-bootstrap';
import { FiEdit2, FiTrash2, FiCopy, FiPlay, FiEye, FiPower } from '../../utils/icons';
import styles from './PaginatedTable.module.css';

interface PaginatedTableProps<T> {
  data: T[];
  columns: Array<{
    key: keyof T;
    title: string;
    render?: (value: any, item: T) => React.ReactNode;
  }>;
  itemsPerPage?: number;
  onView?: (item: T) => void;
  onEdit?: (item: T) => void;
  onDelete?: (item: T) => void;
  onDeactivate?: (item: T) => void;
  onClone?: (item: T) => void;
  onRespond?: (item: T) => void;
  hasQuestions?: (item: T) => boolean;
}

function PaginatedTable<T extends { id?: number | string; is_modelo?: boolean }>({
  data,
  columns,
  itemsPerPage = 10,
  onView,
  onEdit,
  onDelete,
  onDeactivate,
  onClone,
  onRespond,
  hasQuestions,
}: PaginatedTableProps<T>) {
  const [currentPage, setCurrentPage] = React.useState(1);

  const totalPages = Math.ceil(data.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentData = data.slice(startIndex, endIndex);

  const handlePageChange = (page: number) => {
    if (page < 1) page = 1;
    if (page > totalPages) page = totalPages;
    setCurrentPage(page);
  };

  const renderPaginationItems = () => {
    const items = [];
    for (let number = 1; number <= totalPages; number++) {
      items.push(
        <Pagination.Item key={number} active={number === currentPage} onClick={() => handlePageChange(number)}>
          {number}
        </Pagination.Item>,
      );
    }
    return items;
  };

  return (
    <div>
      {/* Tabela para telas grandes */}
      <div className="d-none d-lg-block">
        <Table striped bordered hover responsive>
          <thead>
            <tr>
              {columns.map((col) => (
                <th key={String(col.key)}>{col.title}</th>
              ))}
              {(onView || onEdit || onDelete || onDeactivate || onClone || onRespond) && <th>Ações</th>}
            </tr>
          </thead>
          <tbody>
            {currentData.length > 0 ? (
              currentData.map((item) => (
                <tr key={item.id || Math.random()}>
                  {columns.map((col) => (
                    <td key={String(col.key)}>
                      {col.render
                        ? col.render(item[col.key], item)
                        : String(item[col.key] || '')}
                    </td>
                  ))}
                  {(onView || onEdit || onDelete || onDeactivate || onClone || onRespond) && (
                    <td>
                      {onRespond && !item.is_modelo && hasQuestions && hasQuestions(item) && (
                        <Button variant="outline-success" size="sm" onClick={() => onRespond(item)} title="Responder" className="me-2">
                          <FiPlay size={16} />
                        </Button>
                      )}
                      {onClone && (
                        <Button variant="outline-secondary" size="sm" onClick={() => onClone(item)} title="Clonar" className="me-2">
                          <FiCopy size={16} />
                        </Button>
                      )}
                      {onView && (
                        <Button variant="outline-info" size="sm" onClick={() => onView(item)} title="Visualizar" className="me-2">
                          <FiEye size={16} />
                        </Button>
                      )}
                      {onEdit && (
                        <Button variant="outline-primary" size="sm" onClick={() => onEdit(item)} title="Editar" className="me-2">
                          <FiEdit2 size={16} />
                        </Button>
                      )}
                      {onDeactivate && (
                        <Button variant="outline-warning" size="sm" onClick={() => onDeactivate(item)} title="Desativar" className="me-2">
<FiPower size={16} />
                        </Button>
                      )}
                      {onDelete && (
                        <Button variant="outline-danger" size="sm" onClick={() => onDelete(item)} title="Excluir">
                          <FiTrash2 size={16} />
                        </Button>
                      )}
                    </td>
                  )}
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={columns.length + (onEdit || onDelete ? 1 : 0)} className="text-center">
                  Nenhum item cadastrado ainda.
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>

      {/* Cards para telas pequenas */}
      <div className="d-lg-none">
        {currentData.length > 0 ? (
          currentData.map((item) => (
            <Card key={item.id || Math.random()} className={styles.card}>
              <Card.Body className={styles.cardBody}>
                {columns.map((col) => (
                  <div key={String(col.key)} className={styles.cardRow}>
                    <span className={styles.cardLabel}>{col.title}</span>
                    <span className={styles.cardValue}>
                      {col.render
                        ? col.render(item[col.key], item)
                        : String(item[col.key] || '')}
                    </span>
                  </div>
                ))}
              </Card.Body>
              {(onView || onEdit || onDelete || onDeactivate || onClone || onRespond) && (
                <Card.Footer className={styles.cardFooter}>
                  {onRespond && !item.is_modelo && hasQuestions && hasQuestions(item) && (
                    <Button variant="outline-success" size="sm" onClick={() => onRespond(item)} title="Responder">
                      <FiPlay size={16} />
                    </Button>
                  )}
                  {onClone && (
                    <Button variant="outline-secondary" size="sm" onClick={() => onClone(item)} title="Clonar">
                      <FiCopy size={16} />
                    </Button>
                  )}
                  {onView && (
                    <Button variant="outline-info" size="sm" onClick={() => onView(item)} title="Visualizar">
                      <FiEye size={16} />
                    </Button>
                  )}
                  {onEdit && (
                    <Button variant="outline-primary" size="sm" onClick={() => onEdit(item)} title="Editar">
                      <FiEdit2 size={16} />
                    </Button>
                  )}
                  {onDeactivate && (
                    <Button variant="outline-warning" size="sm" onClick={() => onDeactivate(item)} title="Desativar">
                      <FiPower size={16} />
                    </Button>
                  )}
                  {onDelete && (
                    <Button variant="outline-danger" size="sm" onClick={() => onDelete(item)} title="Excluir">
                      <FiTrash2 size={16} />
                    </Button>
                  )}
                </Card.Footer>
              )}
            </Card>
          ))
        ) : (
          <div className="text-center">Nenhum item cadastrado ainda.</div>
        )}
      </div>

      {totalPages > 1 && (
        <Pagination>
          <Pagination.Prev onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} />
          {renderPaginationItems()}
          <Pagination.Next onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} />
        </Pagination>
      )}
    </div>
  );
}

export default PaginatedTable;